import { HighlightInTextarea } from "../timestamp_sync/highlight_in_textarea.js";

// Cache for word segments by external_id
var wordSegmentsCache = null;

// when liveview patches DOM (e.g. from videoTime event, draft saved message, etc.)
// LV does a setSelectionRange on the focused textarea, which as a side effect reverts the scroll to where the caret / selection is (if the textarea was focused before the patch).
// this means that if the user scrolled away from the caret, the next update (every second with video playing) will forcibly reset the scroll.
// this works around by hooking into LV patch dom onBeforeElUpdated and saving and restoring the scroll position.
export function preserveFocusedTranscriptTextAreaScroll(from) {
  const activeElement = document.activeElement;
  if (
    activeElement?.tagName !== "TEXTAREA" ||
    activeElement.id !== "transcript" ||
    !activeElement.closest(".hit-container") ||
    (from !== activeElement && !from.contains(activeElement))
  ) {
    return;
  }

  const scroll = {
    top: activeElement.scrollTop,
    left: activeElement.scrollLeft,
  };

  const restoreScroll = () => {
    const transcriptTextArea = document.activeElement;
    if (
      transcriptTextArea?.tagName !== "TEXTAREA" ||
      transcriptTextArea.id !== "transcript"
    ) {
      return;
    }

    transcriptTextArea.scrollTop = scroll.top;
    transcriptTextArea.scrollLeft = scroll.left;

    const backdrop = transcriptTextArea
      .closest(".hit-container")
      ?.querySelector(".hit-backdrop");
    if (backdrop) {
      backdrop.scrollTop = scroll.top;
      backdrop.style.transform =
        scroll.left > 0 ? `translateX(${-scroll.left}px)` : "";
    }
  };

  queueMicrotask(restoreScroll);
  requestAnimationFrame(restoreScroll);
}

export const Suggestion = {
  mounted() {
    this.currentScrollPercentageY = 0;
    this.video = document.querySelector("#video");

    // Initialize web workers
    if (
      this.el.dataset.type === "transcript" &&
      this.el.dataset.original_language === this.el.dataset.language
    ) {
      //only same language transcript is possible for highlight, diff-only mode maybe some other time (once people start using it)
      this.initializeWorkers();
    }

    this.bindInputElement();

    this.handleEvent(
      "show_edit_instructions_first_time_if_needed",
      this.showEditInstructionsFirstTimeIfNeeded.bind(this)
    );

    this.handleEvent(
      `start_suggesting_${this.el.dataset.type}`,
      this.startSuggesting.bind(this)
    );

    this.handleEvent(
      "initialize_highlighted_text_area",
      this.initializeHighlightedTextArea.bind(this)
    );

    if (
      this.el.nextElementSibling &&
      this.el.nextElementSibling.querySelector("#transcript-content")
    ) {
      const transcriptContent = this.el.nextElementSibling.querySelector(
        "#transcript-content"
      );
      transcriptContent.addEventListener("scroll", () => {
        this.currentScrollPercentageY =
          (transcriptContent.scrollTop /
            (transcriptContent.scrollHeight - transcriptContent.clientHeight)) *
          100;
      });
    }

    this.handleEvent("show_draft_saved_message", (event) => {
      document.querySelector("#draft-saving-message").classList.add("hidden");
      document.querySelector("#draft-saved-message").classList.remove("hidden");
    });

    this.handleEvent(
      "hide_video_time_highlight",
      this.hideVideoTimeHighlight.bind(this)
    );
  },

  showDraftSavingMessage() {
    requestAnimationFrame(() => {
      //on editor open, the message is not there yet until the user makes a first change
      const draftSavingMessage = document.querySelector("#draft-saving-message");
      if (draftSavingMessage) {
        draftSavingMessage.classList.remove("hidden");
      }
      const draftSavedMessage = document.querySelector("#draft-saved-message");
      if (draftSavedMessage) {
        draftSavedMessage.classList.add("hidden");
      }
    });
  },

  //webworkers to take advantage of multi-threading and make the UI feel less slow
  initializeWorkers() {
    this.combinedWorker = new Worker(`${appBasePath}/assets/realtime_highlight.js`);
    const token = this.el.dataset.token;
    if (token) {
      this.combinedWorker.postMessage({
        //needs to be done on load, otherwise the token can be expired by the time the user starts editing
        type: "fetchWordSegments",
        libUrl: this.el.dataset.lib_url,
        externalId: this.el.dataset.external_id,
        token: token,
      });
    }

    this.combinedWorker.onmessage = (e) => {
      const { type, diffHighlightArray, sentenceHighlightArray } = e.data;

      if (!this.highlightedTextArea) return;

      if (type === "diffResult") {
        this.highlightedTextArea.highlightArrays[0] = diffHighlightArray;
        if (this.video && this.video.paused) {
          this.highlightedTextArea.handleInput();
        }
      } else if (type === "highlightResult") {
        this.highlightedTextArea.highlightArrays[1] = sentenceHighlightArray;
        this.highlightedTextArea.handleInput();
      }
    };
  },

  bindInputElement() {
    this.inputElement =
      this.el.querySelector("input") || this.el.querySelector("textarea");

    if (this.inputElement) {
      this.inputElement.addEventListener(
        "input",
        this.showDraftSavingMessage.bind(this)
      );

      if (
        this.el.dataset.type === "transcript" &&
        this.el.dataset.original_language === this.el.dataset.language
      ) {
        this.inputElement.addEventListener(
          "input",
          this.requestDiffUpdate.bind(this)
        );
      }
    }
  },

  disconnected() {
    if (this.inputElement) {
      this.inputElement.disabled = true;
      this.inputElement.classList.remove("bg-robe-25", "text-robe-900");
      this.inputElement.classList.add(
        "bg-robe-25",
        "text-robe-400",
        "cursor-not-allowed"
      );
    }
  },

  reconnected() {
    if (this.inputElement) {
      this.inputElement.disabled = false;
      this.inputElement.classList.remove(
        "bg-robe-25",
        "text-robe-400",
        "cursor-not-allowed"
      );
      this.inputElement.classList.add("bg-robe-25", "text-robe-900");
    }
  },

  showEditInstructionsFirstTimeIfNeeded() {
    const cookieName = "edit_instructions_shown";
    const maxAge = 30 * 24 * 60 * 60; // 1 month

    const hasSeenInstructions = document.cookie.includes(`${cookieName}=true`);

    if (!hasSeenInstructions) {
      this.video.pause();
      this.pushEvent("toggle_edit_instructions_modal");
    }

    document.cookie = `${cookieName}=true; max-age=${maxAge}; path=/`;
  },

  //on keystroke
  requestDiffUpdate() {
    if (
      !this.transcriptTextArea ||
      !this.highlightedTextArea ||
      !this.combinedWorker
    )
      return;

    this.combinedWorker.postMessage({
      type: "processDiff",
      originalText: this.transcriptTextArea.dataset.originalText,
      currentText: this.transcriptTextArea.value,
    });
  },

  startSuggesting() {
    if (this.currentScrollPercentageY != 0) {
      const transcript = this.el.querySelector("#transcript");
      if (transcript) {
        const scrollPosition =
          (transcript.scrollHeight - transcript.clientHeight) *
          (this.currentScrollPercentageY / 100);
        transcript.scrollTop = scrollPosition;
      }
    }

    this.bindInputElement();
    this.initializeHighlightedTextArea();
  },

  hideVideoTimeHighlight() {
    if (this.highlightedTextArea) {
      this.highlightedTextArea.highlightArrays[1] = [];
      document
        .querySelectorAll("#transcript-highlights .highlight-1")
        .forEach((element) => {
          const textContent = element.textContent;
          element.replaceWith(textContent);
        });
    }
  },

  initializeHighlightedTextArea() {
    if (this.el.dataset.type !== "transcript") return;

    if (!this.highlightedTextArea) {
      const highlightedTextArea = new HighlightInTextarea("#transcript", {
        highlightArrays: [],
      });

      this.highlightedTextArea = highlightedTextArea;
    }

    // Generate initial diff highlights on first load
    this.transcriptTextArea = this.el.querySelector("#transcript");
    this.requestDiffUpdate();
    this.highlightedTextArea.handleInput(); //show the highlights first time
    this.highlightedTextArea.handleScroll(); //toggling between diff and edit mode needs to reset the highlights backdrop scroll

    this.video = document.querySelector("#video");
    if (
      this.el.dataset.type === "transcript" &&
      this.el.dataset.original_language === this.el.dataset.language
    ) {
      this.video.addEventListener("timeupdate", () => {
        if (!this.transcriptTextArea || !this.combinedWorker) return;

        if (
          document.querySelector("#manage-video-player") &&
          document
            .querySelector("#manage-video-player")
            .classList.contains("hidden")
        ) {
          return;
        }

        this.combinedWorker.postMessage({
          type: "processTimeUpdate",
          currentText: this.transcriptTextArea.value,
          timestamp: this.video.currentTime,
          videoDuration: this.video.duration,
          libUrl: this.transcriptTextArea.dataset.libUrl,
          externalId: this.transcriptTextArea.dataset.external_id,
          token: this.transcriptTextArea.dataset.token,
        });
      });
    }
  },

  destroyed() {
    // Clean up web worker
    if (this.combinedWorker) {
      this.combinedWorker.terminate();
      this.combinedWorker = null;
    }
  },

  updated() {
    // only reinitialize if old elements are gone and new elements are present
    const transcriptElement = this.el.querySelector("#transcript");
    const container = transcriptElement?.closest(".hit-container");

    if (
      !document.body.contains(this.transcriptTextArea) &&
      transcriptElement &&
      container
    ) {
      this.highlightedTextArea = null;
      this.bindInputElement();
      this.initializeHighlightedTextArea();
    }
  },
};
